export type Vec3 = readonly [number, number, number];
export type Quat = readonly [number, number, number, number];

export interface TransformSpec {
  position: Vec3;
  rotation: Quat;
  scale: Vec3;
}

export interface MaterialSpec {
  id: string;
  label: string;
  baseColor: string;
  metalness: number;
  roughness: number;
  emissive?: string;
  emissiveIntensity?: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  opacity?: number;
  surfacePattern?: 'fine-resin' | 'brushed-steel' | 'solar-weave';
  normalScale?: number;
  envMapIntensity?: number;
  transmission?: number;
  ior?: number;
  thickness?: number;
}

export type GeometryRecipe =
  | {
      kind: 'box';
      size: Vec3;
    }
  | {
      kind: 'roundedPanel';
      size: Vec3;
      radius: number;
      segments: number;
    }
  | {
      kind: 'chamferedPanel';
      size: Vec3;
      cornerCut: number;
      waistInset: number;
      bevelSize: number;
    }
  | {
      kind: 'cylinder';
      radiusTop: number;
      radiusBottom: number;
      height: number;
      radialSegments: number;
    }
  | {
      kind: 'sphere';
      radius: number;
      widthSegments: number;
      heightSegments: number;
    }
  | {
      kind: 'torus';
      radius: number;
      tube: number;
      radialSegments: number;
      tubularSegments: number;
      arc: number;
    }
  | {
      kind: 'capsule';
      radius: number;
      length: number;
      capSegments: number;
      radialSegments: number;
    }
  | {
      kind: 'fanBlade';
      innerRadius: number;
      outerRadius: number;
      chord: number;
      thickness: number;
      skew: number;
    }
  | {
      kind: 'pixelText';
      text: string;
      width: number;
      height: number;
      depth: number;
    }
  | {
      kind: 'vectorText';
      text: string;
      fontId: 'helvetiker-bold';
      width: number;
      height: number;
      depth: number;
    };

export interface PartSpec {
  id: string;
  label: string;
  semanticClass: string;
  selectable: boolean;
  explodeDirection: Vec3;
  tags: readonly string[];
}

export type SceneNodeSpec =
  | {
      id: string;
      kind: 'group';
      parentNodeId?: string;
      transform: TransformSpec;
    }
  | {
      id: string;
      kind: 'mesh';
      parentNodeId?: string;
      partId: string;
      geometry: GeometryRecipe;
      materialId: string;
      transform: TransformSpec;
      castShadow: boolean;
      receiveShadow: boolean;
    };

export interface RevoluteJointSpec {
  id: string;
  label: string;
  type: 'revolute';
  nodeId: string;
  axis: Vec3;
  min: number;
  max: number;
  defaultValue: number;
}

export type JointSpec = RevoluteJointSpec;

export type ActionSpec =
  | {
      id: string;
      label: string;
      kind: 'spin';
      jointId: string;
      defaultEnabled: boolean;
      defaultValue: number;
      minValue: number;
      maxValue: number;
    }
  | {
      id: string;
      label: string;
      kind: 'oscillate';
      jointId: string;
      defaultEnabled: boolean;
      defaultValue: number;
      minValue: number;
      maxValue: number;
      frequencyHz: number;
    };

export type LocalClockTextFormat = 'HH:MM' | 'SS' | 'DD' | 'M.DD';

export interface LocalClockTextBindingSpec {
  id: string;
  kind: 'local-clock-text';
  nodeId: string;
  format: LocalClockTextFormat;
}

export type RuntimeBindingSpec = LocalClockTextBindingSpec;

export interface RenderProfile {
  background: string;
  floor: string;
  camera: {
    position: Vec3;
    target: Vec3;
    fov: number;
  };
  exposure: number;
}

export interface SceneBudgets {
  maxNodes: number;
  maxTriangles: number;
  maxMaterials: number;
}

export interface SceneSpecV1 {
  schemaVersion: '1.0';
  id: string;
  name: string;
  description: string;
  coordinateSystem: {
    handedness: 'right';
    up: 'Y';
    forward: 'Z';
    units: 'meter';
  };
  source: {
    kind: 'text';
    prompt: string;
    promptDigest: string;
    locale: string;
    planner: string;
    seed: number;
  };
  boundsHint: Vec3;
  budgets: SceneBudgets;
  materials: readonly MaterialSpec[];
  parts: readonly PartSpec[];
  nodes: readonly SceneNodeSpec[];
  joints: readonly JointSpec[];
  actions: readonly ActionSpec[];
  runtimeBindings?: readonly RuntimeBindingSpec[];
  render: RenderProfile;
  assumptions: readonly string[];
}

export const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const identityTransform = (): TransformSpec => ({
  position: [0, 0, 0],
  rotation: [0, 0, 0, 1],
  scale: [1, 1, 1],
});
